import React, { useState, useEffect } from 'react';
import { fetchTickets, fetchSLAAlerts } from '../../api';
import { getMexicoMonthRange } from '../../dateUtils';
import { RefreshCw, Cloud, TrendingUp, AlertCircle } from 'lucide-react';
import StatusChart from '../../components/shared/StatusChart';
import PriorityChart from '../../components/shared/PriorityChart';
import TicketsTrend from '../../components/shared/TicketsTrend';
import TicketsTable from '../../components/shared/TicketsTable';
import DateFilterComponent from '../../components/shared/DateFilterComponent';
import SLAAlertsPanel from '../../components/shared/SLAAlertsPanel';
import SLAAlertsTable from '../../components/shared/SLAAlertsTable';

export default function KloudSaaSGerenteDashboard() {
  const [tickets, setTickets] = useState([]);
  const [slaAlerts, setSlaAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isDarkMode] = useState(true);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  
  // Filtro por defecto: mes corriendo
  const [globalDateFilter, setGlobalDateFilter] = useState({
    type: 'absolute',
    ...getMexicoMonthRange()
  });

  // Filtrar TODOS los tickets de Home Depot (incluyendo incidentes)
  const homeDepotTickets = tickets.filter(ticket => 
    ticket.Cuenta === 'Home Depot' || ticket.Cuenta?.includes('Home Depot')
  );

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [ticketsData, alertsData] = await Promise.all([
          fetchTickets(globalDateFilter),
          fetchSLAAlerts(24) // Últimas 24 horas
        ]);
        setTickets(ticketsData);
        
        // Filtrar solo alertas de Home Depot
        const homeDepotAlerts = alertsData.filter(alert => 
          alert.cuenta_cliente === 'Home Depot' || alert.cuenta_cliente?.includes('Home Depot')
        );
        setSlaAlerts(homeDepotAlerts);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    
    const interval = setInterval(loadData, 180000); // 3 minutos
    return () => clearInterval(interval);
  }, [globalDateFilter]);

  useEffect(() => {
    const controlHeader = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsHeaderVisible(false);
      } else if (currentScrollY < lastScrollY) {
        setIsHeaderVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', controlHeader);
    return () => window.removeEventListener('scroll', controlHeader);
  }, [lastScrollY]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const [ticketsData, alertsData] = await Promise.all([
        fetchTickets(globalDateFilter),
        fetchSLAAlerts(24)
      ]);
      setTickets(ticketsData);
      
      const homeDepotAlerts = alertsData.filter(alert => 
        alert.cuenta_cliente === 'Home Depot' || alert.cuenta_cliente?.includes('Home Depot')
      );
      setSlaAlerts(homeDepotAlerts);
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const cardClasses = 'bg-white/10 backdrop-blur-md border border-white/20';

  // Calcular métricas
  const totalTickets = homeDepotTickets.length;
  const ticketsEnCurso = homeDepotTickets.filter(t => t.Estado === 'En curso').length;
  
  // SLA Primera Respuesta de Domo (campo específico para SaaS)
  // Función auxiliar para convertir tiempo a segundos
  const timeToSeconds = (timeStr) => {
    if (!timeStr) return 0;
    const parts = String(timeStr).split(":").map(Number);
    const hours = parts[0] || 0;
    const minutes = parts[1] || 0;
    const seconds = parts[2] || 0;
    return (hours * 3600) + (minutes * 60) + seconds;
  };

  // Filtrar tickets válidos para SLA (cerrados, solucionados o pendientes por usuario)
  // Solo tickets tipo OPS (no incidentes)
  const ticketsConSLA = homeDepotTickets.filter(t => 
    (t.Estado_de_solicitud === 'Cerrado' ||
    t.Estado_de_solicitud === 'Solucionado' ||
    t.Estado_de_solicitud === 'Pendiente por usuario') &&
    t.Tipo_de_ticket !== 'Incidente'
  );
  
  // Calcular cumplidos y quemados comparando tiempo transcurrido vs SLA
  const slaCumplido = ticketsConSLA.filter(t => {
    const responseTranscurrido = timeToSeconds(t.Tiempo_de_respuesta_transcurrido);
    let responseSLA = timeToSeconds(t.Tiempo_de_respuesta_de_SLA);
    
    // Si SLA es 0, considerarlo como un valor muy alto (99 horas)
    if (responseSLA === 0) {
      responseSLA = 99 * 60 * 60;
    }
    
    return responseTranscurrido <= responseSLA;
  }).length;
  
  const slaQuemado = ticketsConSLA.filter(t => {
    const responseTranscurrido = timeToSeconds(t.Tiempo_de_respuesta_transcurrido);
    let responseSLA = timeToSeconds(t.Tiempo_de_respuesta_de_SLA);
    
    // Si SLA es 0, considerarlo como un valor muy alto (99 horas)
    if (responseSLA === 0) {
      responseSLA = 99 * 60 * 60;
    }
    
    return responseTranscurrido > responseSLA;
  }).length;
  
  // Si no hay tickets con SLA, mostrar 100%. Si hay tickets, calcular el porcentaje
  const slaPorcentaje = ticketsConSLA.length > 0 
    ? ((slaCumplido / ticketsConSLA.length) * 100).toFixed(2)
    : 100;
  
  // Debug: ver valores de SLA
  console.log('Tickets Home Depot (TODOS):', homeDepotTickets.length);
  console.log('Tickets tipo OPS para SLA:', ticketsConSLA.length);
  console.log('SLA Cumplido:', slaCumplido);
  console.log('SLA Quemado:', slaQuemado);
  if (ticketsConSLA.length > 0) {
    console.log('Ejemplo de tickets OPS:', ticketsConSLA.slice(0, 3).map(t => ({
      id: t.ID,
      tipo: t.Tipo_de_ticket,
      transcurrido: t.Tiempo_de_respuesta_transcurrido,
      sla: t.Tiempo_de_respuesta_de_SLA,
      estado: t.Estado_de_solicitud
    })));
  }

  // Tickets con incumplimiento de SLA
  const ticketsIncumplimiento = ticketsConSLA.filter(t => {
    const responseTranscurrido = timeToSeconds(t.Tiempo_de_respuesta_transcurrido);
    let responseSLA = timeToSeconds(t.Tiempo_de_respuesta_de_SLA);
    
    if (responseSLA === 0) {
      responseSLA = 99 * 60 * 60;
    }
    
    return responseTranscurrido > responseSLA;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#210056] via-[#4a0080] to-[#7f1ffc] flex items-center justify-center">
        <div className="text-white text-xl">Cargando datos...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#210056] via-[#4a0080] to-[#7f1ffc] text-white">
      {/* Header */}
      <div 
        className={`sticky top-0 z-30 ${cardClasses} backdrop-blur-md transition-transform duration-300 ${
          isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
        }`}
        onMouseEnter={() => setIsHeaderVisible(true)}
      >
        <div className="absolute -bottom-2 left-0 right-0 h-2 bg-transparent"></div>
        
        <div className="flex justify-between items-center px-6 py-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center space-x-3">
              <Cloud className="w-8 h-8 text-[#c88dff]" />
              <span className="bg-gradient-to-r from-[#c88dff] to-[#ff0080] bg-clip-text text-transparent">Kloud Público SaaS - Vista Gerencial</span>
            </h1>
            <p className="text-sm opacity-70 mt-1">Home Depot - Métricas y Análisis</p>
          </div>
          
          <div className="flex items-center space-x-4">
            <DateFilterComponent
              currentFilter={globalDateFilter}
              onDateFilterChange={setGlobalDateFilter}
              cardClasses={cardClasses}
              isDarkMode={isDarkMode}
            />
            
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className={`p-3 rounded-lg ${cardClasses} hover:bg-white/20 transition-all duration-200 ${
                refreshing ? 'animate-spin' : ''
              }`}
              title="Actualizar datos"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Panel de Alertas de SLA */}
        {slaAlerts.length > 0 && (
          <SLAAlertsPanel alerts={slaAlerts} cardClasses={cardClasses} />
        )}

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className={`${cardClasses} p-6 rounded-xl`}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold opacity-70">Total Tickets</h3>
              <Cloud className="w-5 h-5 opacity-50" />
            </div>
            <p className="text-4xl font-bold">{totalTickets}</p>
            <p className="text-xs opacity-50 mt-1">Home Depot</p>
          </div>

          <div className={`${cardClasses} p-6 rounded-xl`}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold opacity-70">En Curso</h3>
              <TrendingUp className="w-5 h-5 opacity-50" />
            </div>
            <p className="text-4xl font-bold">{ticketsEnCurso}</p>
            <p className="text-xs opacity-50 mt-1">Tickets activos</p>
          </div>

          <div className={`${cardClasses} p-6 rounded-xl`}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold opacity-70">SLA Primera Respuesta</h3>
              <AlertCircle className="w-5 h-5 opacity-50" />
            </div>
            <p className="text-4xl font-bold text-green-400">{slaPorcentaje}%</p>
            <p className="text-xs opacity-50 mt-1">
              Cumplido: {slaCumplido} | Quemado: {slaQuemado}
            </p>
          </div>

          <div className={`${cardClasses} p-6 rounded-xl`}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold opacity-70">Incumplimientos</h3>
              <AlertCircle className="w-5 h-5 text-red-400" />
            </div>
            <p className="text-4xl font-bold text-red-400">{ticketsIncumplimiento.length}</p>
            <p className="text-xs opacity-50 mt-1">SLA Quemado</p>
          </div>
        </div>

        {/* Gráficas */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className={`${cardClasses} p-6 rounded-xl`}>
            <StatusChart tickets={homeDepotTickets} />
          </div>
          <div className={`${cardClasses} p-6 rounded-xl`}>
            <PriorityChart tickets={homeDepotTickets} />
          </div>
        </div>

        {/* Tendencia de Tickets por Prioridad */}
        <div className={`${cardClasses} p-6 rounded-xl`}>
          <TicketsTrend tickets={homeDepotTickets} />
        </div>

        {/* Tabla de Alertas de SLA */}
        {slaAlerts.length > 0 && (
          <SLAAlertsTable alerts={slaAlerts} cardClasses={cardClasses} />
        )}

        {/* Tickets con Incumplimiento de SLA */}
        {ticketsIncumplimiento.length > 0 && (
          <div className={`${cardClasses} p-6 rounded-xl`}>
            <h2 className="text-xl font-bold mb-4 flex items-center space-x-2">
              <AlertCircle className="w-6 h-6 text-red-400" />
              <span>Tickets con Incumplimiento de SLA de Primera Respuesta ({ticketsIncumplimiento.length})</span>
            </h2>
            <TicketsTable tickets={ticketsIncumplimiento} />
          </div>
        )}

        {/* Todos los Tickets */}
        <div className={`${cardClasses} p-6 rounded-xl`}>
          <h2 className="text-xl font-bold mb-4">Todos los Tickets - Home Depot</h2>
          <TicketsTable tickets={homeDepotTickets} />
        </div>
      </div>
    </div>
  );
}
